import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Mock database of vehicle registrations
// In a real application, this would come from a backend API
const vehicleDatabase = [
  {
    registrationNumber: 'MH02AB1234',
    ownerName: 'Rahul Sharma',
    block: 'A',
    flatNumber: '12',
    mobileNumber: '9876543210',
    vehicleType: '4-Wheeler',
    make: 'Honda',
    model: 'City',
    color: 'White'
  },
  {
    registrationNumber: 'MH02CD5678',
    ownerName: 'Priya Patel',
    block: 'B',
    flatNumber: '24',
    mobileNumber: '8765432109',
    vehicleType: '2-Wheeler',
    make: 'Hero',
    model: 'Splendor',
    color: 'Black'
  },
  {
    registrationNumber: 'MH02EF9012',
    ownerName: 'Amit Kumar',
    block: 'C',
    flatNumber: '8',
    mobileNumber: '7654321098',
    vehicleType: '4-Wheeler',
    make: 'Maruti',
    model: 'Swift',
    color: 'Red'
  }
];

const VehicleLookupPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Add animation classes to Tailwind CSS
  useEffect(() => {
    const addAnimationClasses = () => {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
      `;
      document.head.appendChild(style);
    };
    
    addAnimationClasses();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Reset states
    setError('');
    setSearchResult(null);
    setHasSearched(true);
    setIsLoading(true);
    
    // Format the search query (remove spaces, convert to uppercase)
    const formattedQuery = searchQuery.replace(/\s+/g, '').toUpperCase();
    
    // Validate registration number format (e.g., MH02AB1234)
    if (!/^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/.test(formattedQuery)) {
      setIsLoading(false);
      setError('Please enter a valid registration number (e.g., MH02AB1234)');
      return;
    }
    
    // Simulate API call with timeout
    setTimeout(() => {
      // Search in the mock database
      const result = vehicleDatabase.find(
        vehicle => vehicle.registrationNumber.replace(/\s+/g, '').toUpperCase() === formattedQuery
      );
      
      if (result) {
        setSearchResult(result);
      } else {
        setError('No vehicle found with this registration number');
      }
      
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back to Home Button */}
      <div className="flex justify-start mb-6">
        <Link to="/" className="text-blue-600 hover:text-blue-800 flex items-center bg-white py-2 px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Home
        </Link>
      </div>
      
      <div className="transform transition-all duration-500 opacity-100 translate-y-0">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700 mb-3">Vehicle Owner Lookup</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 max-w-lg mx-auto">Find vehicle owner information by entering the registration number</p>
        </div>
        
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl p-8 mb-8 border border-blue-100">
          <form onSubmit={handleSearch} className="mb-6">
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2" htmlFor="registrationNumber">
                Vehicle Registration Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3a1 1 0 001-1v-3a1 1 0 00-.293-.707l-2-2A1 1 0 0012 7h-1V5a1 1 0 00-1-1H3z" />
                  </svg>
                </div>
                <input
                  id="registrationNumber"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="shadow-sm border border-gray-300 rounded-lg w-full py-3.5 pl-10 pr-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter registration number (e.g., MH02AB1234)"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Search
                  </>
                )}
              </button>
            </div>
          </form>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded animate-fade-in" role="alert">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}
          
          {searchResult && (
            <div 
              className="bg-white p-6 rounded-xl border border-blue-100 shadow-md animate-fade-in transition-all duration-500 ease-in-out"
            >
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Vehicle Owner Details</h2>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-lg mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Registration Number</p>
                    <p className="font-bold text-lg">{searchResult.registrationNumber}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Owner Name</p>
                    <p className="font-bold text-lg">{searchResult.ownerName}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Residence</p>
                    <p className="font-bold text-lg">Block {searchResult.block}, Flat {searchResult.flatNumber}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Contact Number</p>
                    <p className="font-bold text-lg">{searchResult.mobileNumber}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Vehicle Type</p>
                    <p className="font-bold text-lg">{searchResult.vehicleType}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Vehicle Details</p>
                    <p className="font-bold text-lg">{searchResult.make} {searchResult.model}, {searchResult.color}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {hasSearched && !searchResult && !error && !isLoading && (
            <div className="text-center py-6 animate-fade-in">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 text-lg">No results found. Please try a different registration number.</p>
            </div>
          )}
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100 text-center text-sm text-gray-700">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Privacy Notice</span>
            </div>
            <p>This service is for authorized personnel only. The information provided should be used in accordance with privacy policies.</p>
          </div>
        </div>
        
        {/* Back to Home Button */}
        <div className="flex justify-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 flex items-center bg-white py-2 px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VehicleLookupPage;
